import { useEffect, useState } from "react";
import styled from "styled-components";
import db from "../../config/firebase.config";
import { AllComments } from "./AllComments";
import { IconGrp } from "./IconGrp";
import { Link, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { UserNameWithBadge } from "../common/UserNameWithBadge";

dayjs.extend(relativeTime);

const PostHeader = ({ post }) => {
    const user = useSelector(selectUser);
    const [following, setFollowing] = useState(false);

    const authorName = post.admin_name || (post.admin_email ? post.admin_email.split("@")[0] : "Fellow");
    const isSelf = user?.uid && post.q_user_id === user.uid;

    const toggleFollow = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setFollowing(!following);
    };

    return (
        <div className="header">
            <img
                src={
                    post.admin_img ||
                    "https://qsfs.fs.quoracdn.net/-4-images.new_grid.profile_default.png-26-688c79556f251aa0.png"
                }
                alt="avatar"
            />
            <div className="user">
                <div className="user-line">
                    <UserNameWithBadge
                        name={authorName}
                        uid={post.q_user_id}
                        email={post.admin_email}
                        badgeSize="sm"
                        className="author-name"
                    />
                    {!isSelf && (
                        <button
                            onClick={toggleFollow}
                            className={`follow-btn ${following ? "following" : ""}`}
                        >
                            {following ? "✓ Following" : "+ Follow"}
                        </button>
                    )}
                </div>
                <span className="post-time">
                    {post.created_at
                        ? dayjs(post.created_at).fromNow()
                        : "Just now"}
                </span>
            </div>
        </div>
    );
};

export const QandA = ({ flag }) => {
    const [showComments, setShowComments] = useState("");
    const [commentToogle, setCommentToogle] = useState(false);
    const [allQuestion, setAllQuestions] = useState([]);

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const activeSpace = params.get("space");

    useEffect(() => {
        const dbRef = db.collection("questions/");
        const data = dbRef.onSnapshot((querySnapshot) => {
            const questions = [];
            querySnapshot.forEach((doc) => {
                questions.push({
                    ...doc.data(),
                    key: doc.id,
                    createdAt: doc.data().createdAt,
                });
            });
            const getTimestamp = (item) => item.createdAt || item.created_at || 0;
            setAllQuestions(
                !flag
                    ? questions.sort((a, b) => getTimestamp(b) - getTimestamp(a))
                    : questions
                          .filter((el) => el.isAnswered === false)
                          .sort((a, b) => getTimestamp(b) - getTimestamp(a))
            );
        });

        return () => data();
    }, [flag]);

    const handleComments = (id) => {
        if (showComments === id && commentToogle) {
            setCommentToogle(false);
            setShowComments("");
        } else {
            setShowComments(id);
            setCommentToogle(true);
        }
    };

    const visibleQuestions = activeSpace
        ? allQuestion.filter((el) =>
              (el.tags || []).some(
                  (t) => t.toLowerCase() === activeSpace.toLowerCase()
              )
          )
        : allQuestion;

    return (
        <>
            {activeSpace && (
                <FilterBanner>
                    <span>
                        Showing posts about <strong>{activeSpace}</strong>
                    </span>
                    <Link to="/home">Show all</Link>
                </FilterBanner>
            )}

            {visibleQuestions.map((e) => (
                <Container key={e.key}>
                    <PostHeader post={e} />
                    <div className="content">
                        <div className="textContent">
                            <Link to={`question/${e.key}`}>
                                <h4 className="post-title">{e.question}</h4>
                            </Link>

                            {e.images && e.images.length > 0 && e.images[0] && (
                                <div className="post-image-container">
                                    <img
                                        src={e.images[0]}
                                        alt="Post attachment"
                                        className="post-image"
                                    />
                                </div>
                            )}

                            <IconGrp
                                handleComments={handleComments}
                                toogle={commentToogle}
                                showComments={showComments}
                                id={e.key}
                                questionId={e.key}
                            />
                        </div>
                    </div>

                    {showComments === e.key && commentToogle && (
                        <AllComments
                            questionId={e.key}
                            questionUserId={e.q_user_id}
                        />
                    )}
                </Container>
            ))}
        </>
    );
};

const Container = styled.div`
    width: 100%;
    margin: 0 0 1rem 0;
    background-color: #ffffff !important;
    border: 1px solid rgba(201, 160, 99, 0.25);
    box-shadow: 0 4px 20px rgba(10, 20, 40, 0.06);
    border-radius: 8px;
    overflow: hidden;

    .header {
        padding: 16px 20px 6px 20px;
        display: flex;
        align-items: center;
        gap: 12px;

        img {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            object-fit: cover;
            border: 1.5px solid rgba(201, 160, 99, 0.3);
        }

        .user {
            display: flex;
            flex-direction: column;

            .user-line {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .author-name {
                font-size: 23px;
                font-weight: 700;
                color: #0a1428;
            }

            .follow-btn {
                font-size: 18px;
                font-weight: 700;
                color: #1DA1F2;
                background: none;
                border: none;
                cursor: pointer;
                padding: 3px 10px;
                border-radius: 12px;
                transition: background 0.15s;

                &:hover {
                    background: #e8f5fe;
                }

                &.following {
                    color: #22c55e;
                    background: #f0fdf4;
                }
            }

            .post-time {
                font-size: 17px;
                color: #888;
                margin-top: 1px;
            }
        }
    }

    .content {
        .textContent {
            padding: 10px 20px 14px 20px;

            .post-title {
                font-size: 25px;
                font-weight: 700;
                color: #14171a;
                line-height: 1.35;
                margin-bottom: 8px;

                &:hover {
                    color: #1DA1F2;
                }
            }

            .post-image-container {
                margin: 10px 0 12px 0;
                border-radius: 10px;
                overflow: hidden;

                .post-image {
                    width: 100%;
                    max-height: 420px;
                    object-fit: cover;
                    border-radius: 10px;
                    border: 1px solid rgba(201, 160, 99, 0.2);
                }
            }
        }
    }
`;

const FilterBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: linear-gradient(135deg, #fff8ec, #fdf3df);
    border: 1px solid rgba(201, 160, 99, 0.35);
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 1rem;
    font-size: 18px;
    color: #6b4e2a;

    strong {
        color: #0a1428;
        font-weight: 700;
    }

    a {
        color: #1DA1F2;
        font-weight: 700;
        text-decoration: none;
        white-space: nowrap;

        &:hover {
            text-decoration: underline;
        }
    }
`;
